import { Button } from '@1trading/react/components/ui/button';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@1trading/react/components/ui/form';
import { Loading } from '@1trading/react/components/ui/loading';
import { RadioGroup, RadioGroupItem, RadioGroupItemCard } from '@1trading/react/components/ui/radio-group';
import { useToast } from '@1trading/react/hooks/use-toast';
import { standardSchemaResolver } from '@hookform/resolvers/standard-schema';
import { type UseFormReturn, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingTitle } from '@/components/ui/page-heading';
import { CONTROL_TYPES, ControlType } from '@/constants/institution';
import InstitutionGoToReviewButton from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import InstitutionFuturesAlgorithm from '@/features/institution/futures/components/algorithm';
import InstitutionFuturesIndividuals from '@/features/institution/futures/components/individuals';
import {
  type InstitutionFuturesDecisionMaker,
  type InstitutionFuturesDecisionMakerOrDefaults,
  institutionDetailsDecisionMakerApiToInstitutionFuturesDecisionMaker,
  institutionFuturesDecisionMakerSchema,
  institutionFuturesDecisionMakerToInstitutionDetailsDecisionMakerApi,
} from '@/features/institution/futures/decision-maker/schema';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsMutation, useInstitutionDetailsV2Answers } from '@/features/institution/hooks/useInstitutionDetails';
import { useInstitutionOnboardingNextDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingNextDetailsRoute';
import { useInstitutionOnboardingPrevDetailsRoute } from '@/features/institution/hooks/useInstitutionOnboardingPrevDetailsRoute';
import { reportIndividualNationalClientIdentifierFormatErrors } from '@/features/institution/schemas/individual';
import { INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';

const InstitutionFuturesDecisionMakerOnboardingFormForm = ({ defaultValues, onPrevStep }: { defaultValues: InstitutionFuturesDecisionMakerOrDefaults; onPrevStep: () => void }) => {
  const form = useForm<InstitutionFuturesDecisionMaker>({
    defaultValues,
    resolver: standardSchemaResolver(institutionFuturesDecisionMakerSchema),
  });
  const { isPending, mutate } = useInstitutionDetailsMutation();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const getNextDetailsRoute = useInstitutionOnboardingNextDetailsRoute('decisionMaker');
  const navigate = useNavigate();
  const { toast } = useToast();
  const { t } = useTranslation();
  const decisionMaker = form.watch('decisionMaker');

  const onSubmit = (data: InstitutionFuturesDecisionMaker, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    const savedAnswers = { decisionMaker: institutionFuturesDecisionMakerToInstitutionDetailsDecisionMakerApi(data) };

    mutate(savedAnswers, {
      onError: () => {
        toast({
          description: t('fundamentals.defaultError.message'),
          variant: 'destructive',
        });
      },
      onSuccess: () => {
        navigate(isReturningToReview ? `${onboardingPath}/review` : getNextDetailsRoute(savedAnswers), { replace: true });
      },
    });
  };

  const onInvalid = () => reportIndividualNationalClientIdentifierFormatErrors(form.getValues('individuals'));

  return (
    <Form {...form}>
      <div className="flex flex-row justify-between -md:justify-end">
        <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={onPrevStep} />
        <InstitutionGoToReviewButton onSubmit={() => form.handleSubmit((data) => onSubmit(data, { isReturningToReview: true }))()} />
      </div>
      <PageCard size="lg" bordered fixedFooter>
        <form name="institution-decision-maker" onSubmit={form.handleSubmit((data) => onSubmit(data), onInvalid)}>
          <PageCardContent className="pt-8 space-y-6">
            <FormField
              name="decisionMaker"
              control={form.control}
              render={({ field }) => (
                <FormItem>
                  <FormLabel className="sr-only">{t('institutionDetails.decisionMaker.decisionMaker')}</FormLabel>
                  <FormControl>
                    <RadioGroup
                      defaultValue={field.value}
                      variant="cards"
                      aria-label={t('institutionDetails.decisionMaker.decisionMaker')}
                      onValueChange={(value) => {
                        field.onChange(value);
                        (form as UseFormReturn<InstitutionFuturesDecisionMakerOrDefaults>).setValue(value === ControlType.INDIVIDUAL ? 'algorithm' : 'individuals', undefined);
                      }}
                    >
                      {CONTROL_TYPES.map((answer) => (
                        <FormItem key={answer}>
                          <RadioGroupItemCard variant="outline">
                            <FormControl>
                              <RadioGroupItem value={answer} />
                            </FormControl>
                            <FormLabel contained>{t(`institutionDetails.controlType.options.${answer}`)}</FormLabel>
                          </RadioGroupItemCard>
                        </FormItem>
                      ))}
                    </RadioGroup>
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            {{
              [ControlType.ALGORITHM]: <InstitutionFuturesAlgorithm />,
              [ControlType.INDIVIDUAL]: <InstitutionFuturesIndividuals section="decisionMaker" />,
            }[decisionMaker] ?? null}
          </PageCardContent>
          <PageCardFooter>
            <Button className="relative w-full" size="lg" type="submit" variant="primary" disabled={isPending}>
              <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
              {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
            </Button>
          </PageCardFooter>
        </form>
      </PageCard>
    </Form>
  );
};
InstitutionFuturesDecisionMakerOnboardingFormForm.displayName = 'InstitutionFuturesDecisionMakerOnboardingFormForm';

const InstitutionFuturesDecisionMakerOnboardingForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'decisionMaker');
  const prevDetailsRoute = useInstitutionOnboardingPrevDetailsRoute('decisionMaker');
  const navigate = useNavigate();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.decisionMaker
    ? institutionDetailsDecisionMakerApiToInstitutionFuturesDecisionMaker(institutionDetailsAnswers.decisionMaker)
    : {};

  return (
    <section className="relative">
      <PageHeading>
        <PageHeadingTitle>{t('institutionDetails.decisionMaker.title')}</PageHeadingTitle>
      </PageHeading>
      <InstitutionFuturesDecisionMakerOnboardingFormForm defaultValues={defaultValues} onPrevStep={() => navigate(prevDetailsRoute, { replace: true })} />
    </section>
  );
};
InstitutionFuturesDecisionMakerOnboardingForm.displayName = 'InstitutionFuturesDecisionMakerOnboardingForm';

export default InstitutionFuturesDecisionMakerOnboardingForm;
